import { useState, useEffect, useCallback } from 'react';
import { getUnifiedRoundTimerState, isRoundActive, GameType, } from '@swtorbit/shared-socket-events';
/**
 * Format seconds into MM:SS display string
 */
export function formatTime(seconds) {
    const mins = Math.floor(Math.abs(seconds) / 60);
    const secs = Math.abs(seconds) % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
/**
 * Format milliseconds into MM:SS.d display string (tenths of a second).
 * For race-style timers where sub-second precision matters.
 */
export function formatTimeWithTenths(ms) {
    const abs = Math.abs(ms);
    const totalSeconds = Math.floor(abs / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const tenths = Math.floor((abs % 1000) / 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${tenths}`;
}
/**
 * @deprecated Use formatTime instead
 */
export const formatTimeDisplay = formatTime;
/**
 * React hook for managing round timer state with automatic updates.
 * Supports all game types - automatically dispatches to the correct
 * timer calculation based on the round's game type.
 *
 * @param round - The current round (or null/undefined if no round)
 * @param updateIntervalMs - How often to recalculate (default: 1000ms)
 * @param localStartTime - Optional local timestamp when round started (for timezone-safe timing)
 * @returns Timer state with UI-friendly formatted values
 */
export function useRoundTimer(round, updateIntervalMs = 1000, localStartTime) {
    const [timerState, setTimerState] = useState(null);
    // Calculate timer state using unified function
    const updateTimerState = useCallback(() => {
        if (!round) {
            setTimerState(null);
            return;
        }
        // If localStartTime provided, create a modified round with that as startedAt
        // This ensures all timing calculations use the local clock
        const effectiveRound = localStartTime
            ? { ...round, startedAt: localStartTime }
            : round;
        const state = getUnifiedRoundTimerState(effectiveRound, Date.now());
        setTimerState(state);
    }, [round, localStartTime]);
    // Set up interval for updates
    useEffect(() => {
        // Initial calculation
        updateTimerState();
        // Only set up interval if round is in an active phase
        if (!round || !isRoundActive(round)) {
            setTimerState(null);
            return;
        }
        const interval = setInterval(updateTimerState, updateIntervalMs);
        return () => clearInterval(interval);
    }, [round, updateIntervalMs, updateTimerState]);
    // Inactive state
    if (!timerState) {
        return {
            timerState: null,
            displayTime: '--:--',
            maxDisplayTime: '',
            isUnlimited: false,
            progress: 0,
            isActive: false,
            phase: 'inactive',
            gameType: null,
            hnsTimerState: null,
            fprTimerState: null,
        };
    }
    const isUnlimited = timerState.secondsMax === 0;
    // Display time - use the unified fields
    // For countdown phases (hiding, countdown), show remaining time
    // For active phases (seeking, racing), show elapsed time
    let displayTime;
    if (timerState.phase === 'hiding' || timerState.phase === 'countdown') {
        displayTime = formatTime(timerState.secondsRemaining);
    }
    else {
        displayTime = formatTime(timerState.secondsElapsed);
    }
    // Max display time (only if not unlimited)
    const maxDisplayTime = isUnlimited ? '' : formatTime(timerState.secondsMax);
    // Progress calculation (only meaningful during active phase with time limit)
    let progress = 0;
    if (timerState.phase !== 'hiding' && timerState.phase !== 'countdown' && !isUnlimited && timerState.secondsMax > 0) {
        progress = Math.min(100, (timerState.secondsElapsed / timerState.secondsMax) * 100);
    }
    // Type-narrow for game-specific accessors
    const hnsTimerState = timerState.gameType === GameType.HIDE_AND_SEEK ? timerState : null;
    const fprTimerState = timerState.gameType === GameType.FP_RACE ? timerState : null;
    return {
        timerState,
        displayTime,
        maxDisplayTime,
        isUnlimited,
        progress,
        isActive: true,
        phase: timerState.phase,
        gameType: timerState.gameType,
        hnsTimerState,
        fprTimerState,
    };
}
